import {
  Box,
  Divider,
  Tooltip,
  Typography,
  makeStyles,
  useTheme
} from '@material-ui/core';
import {
  SText,
  SCheckbox,
  SETVIImage,
  SHighlightText
} from '@setvi/shared/components';
import { TextColor } from '@setvi/shared/enums';
import { QuoteProductDTO } from '@setvi/shared/services/react-query';
import { ImagePreview } from 'componentsV2';

const useStyles = makeStyles(() => ({
  itemRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    minHeight: 60
  },
  checkboxWrapper: {
    height: 60,
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0
  },
  imageColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0
  },
  productDetails: {
    flex: 1,
    display: 'flex',
    gap: '12px',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: 0,
    height: 60
  },
  optionsBadge: {
    display: 'flex',
    minWidth: 64,
    width: 'fit-content',
    padding: '4px 8px',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    borderRadius: 16,
    backgroundColor: '#F8F9FA',
    cursor: 'default'
  },
  optionsBadgeText: {
    color: '#5D7083',
    textAlign: 'center',
    fontFamily: 'Roboto',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '16px',
    letterSpacing: 0,
    whiteSpace: 'nowrap'
  },
  optionsTooltip: {
    borderRadius: 8,
    padding: '10px 12px',
    maxWidth: 360
  },
  optionsTooltipItem: {
    fontSize: '12px',
    lineHeight: '20px',
    color: '#FFFFFF',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 336
  }
}));

interface SelectableItemRowProps {
  product: QuoteProductDTO;
  searchQuery?: string;
  selected: boolean;
  onToggle: () => void;
}

export const SelectableItemRow = ({
  product,
  searchQuery,
  selected,
  onToggle
}: SelectableItemRowProps) => {
  const classes = useStyles();
  const { palette } = useTheme();

  const imageSrc =
    product?.imageUrl ||
    product?.images?.[0]?.path ||
    '/images/product-placeholder.svg';

  const labelText = product?.customLabel || product?.labelType;
  const truncatedLabel =
    labelText && labelText.length > 10
      ? `${labelText.slice(0, 9)}..`
      : labelText;

  const options = product?.children ?? [];
  const hasOptions = options.length > 0;
  const hasNote = !!product?.note?.trim();

  return (
    <Box>
      <Box className={classes.itemRow}>
        <Box className={classes.checkboxWrapper}>
          <SCheckbox checked={selected} onChange={onToggle} />
        </Box>

        <Box className={classes.imageColumn}>
          <ImagePreview
            title={
              <img
                src={imageSrc}
                alt="product-img"
                onError={e => {
                  e.currentTarget.src = '/images/product-placeholder.svg';
                }}
                style={{
                  width: 180,
                  height: 180,
                  objectFit: 'contain',
                  backgroundColor: palette.common.white,
                  borderRadius: 10,
                  padding: 20
                }}
              />
            }
            placement="right"
            arrow>
            <Box
              minWidth={60}
              minHeight={60}
              width={60}
              display="flex"
              alignItems="center"
              justifyContent="center">
              <SETVIImage
                src={imageSrc}
                alt={product?.name || 'Product'}
                width={60}
                height={60}
                radius={10}
                fit="contain"
                style={{
                  maxHeight: 60,
                  maxWidth: 60,
                  minHeight: 60,
                  zIndex: 2,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #AFBAC6'
                }}
              />
            </Box>
          </ImagePreview>
        </Box>

        <Box className={classes.productDetails}>
          <SHighlightText
            value={product.name}
            weight="semibold"
            searchQuery={searchQuery}
            maxLines={2}
          />
          <Box display="flex" alignItems="center" gridGap={5}>
            <SHighlightText
              size="sm"
              fontColor={TextColor.SlateGrey}
              value={`SKU: ${product.sku}`}
              searchQuery={searchQuery}
            />
            <Divider orientation="vertical" flexItem />
            <SHighlightText
              size="sm"
              fontColor={TextColor.SlateGrey}
              value={`MFG: ${product?.vendor?.name || 'N/A'}`}
              searchQuery={searchQuery}
            />
          </Box>
        </Box>
      </Box>

      {(truncatedLabel || hasNote || hasOptions) && (
        <Box style={{ marginLeft: 40, marginTop: 10 }}>
          {hasNote && (
            <>
              <Divider />
              <SText
                prefix="Note: "
                title={product.note ?? ''}
                fontColor="#333D47"
                maxLines={2}
                style={{ padding: '8px 0 0' }}
              />
            </>
          )}
          {(hasOptions || truncatedLabel) && (
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: hasNote ? 8 : 0
              }}>
              {/* Subitems indicator: shows the option count with the full
                  option names on hover so users can see them before moving the
                  product into a section (QOT-1536). */}
              {hasOptions && (
                <Tooltip
                  arrow
                  placement="bottom-start"
                  classes={{ tooltip: classes.optionsTooltip }}
                  title={
                    <Box>
                      {options.map((option, index) => (
                        <Typography
                          key={option.id || option._id}
                          className={classes.optionsTooltipItem}>
                          {`${index + 1}. ${option.name}`}
                        </Typography>
                      ))}
                    </Box>
                  }>
                  <Box className={classes.optionsBadge}>
                    <Typography className={classes.optionsBadgeText}>
                      {`+${options.length} ${
                        options.length === 1 ? 'Option' : 'Options'
                      } available`}
                    </Typography>
                  </Box>
                </Tooltip>
              )}
              {truncatedLabel && (
                <Box className={classes.optionsBadge}>
                  <Typography className={classes.optionsBadgeText}>
                    {truncatedLabel.charAt(0).toUpperCase() +
                      truncatedLabel.slice(1)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
